// client/src/components/admin/analytics/HealthMonitoringPanel.js
import React from 'react'
import PropTypes from 'prop-types'
import {
  getHealthStatusColor,
  getHealthStatusIcon,
} from '../../../utils/analyticsUtils'

/**
 * System health monitoring panel component.
 * Displays health checks, performance metrics, and overall system status.
 *
 * @param {Object} healthData - Health check data from API (optional)
 * @param {Object} performanceData - Performance metrics data from API (optional)
 * @param {Boolean} isLoading - Loading state
 * @param {Function} onRefresh - Handler for manual refresh
 */
function HealthMonitoringPanel({
  healthData = null,
  performanceData = null,
  isLoading,
  onRefresh,
}) {
  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">🔍 System Health</h5>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={onRefresh}
              disabled={isLoading}
              data-testid="health-refresh-button"
            >
              {isLoading ? 'Checking...' : 'Refresh'}
            </button>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="text-center py-3" data-testid="health-loading">
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                >
                  <span className="visually-hidden">
                    Loading health data...
                  </span>
                </div>
                <span className="ms-2">Checking system health...</span>
              </div>
            ) : healthData && performanceData ? (
              <div className="row" data-testid="health-content">
                {/* Overall Status */}
                <div className="col-12 col-md-3 mb-3">
                  <div
                    className={`text-center p-3 rounded bg-${getHealthStatusColor(
                      healthData.status,
                    )} bg-opacity-10`}
                    data-testid="overall-status"
                  >
                    <div className="fs-2 mb-2">
                      {getHealthStatusIcon(healthData.status)}
                    </div>
                    <h6
                      className={`text-${getHealthStatusColor(
                        healthData.status,
                      )} mb-1`}
                    >
                      {healthData.status.toUpperCase()}
                    </h6>
                    <small className="text-muted">Overall Status</small>
                  </div>
                </div>

                {/* Health Checks */}
                <div className="col-12 col-md-9">
                  <div className="row">
                    {Object.entries(healthData.checks || {}).map(
                      ([checkName, check]) => (
                        <div
                          key={checkName}
                          className="col-12 col-md-4 mb-3"
                          data-testid={`health-check-${checkName}`}
                        >
                          <div className="health-check-item">
                            <div className="d-flex align-items-center mb-1">
                              <span className="me-2">
                                {getHealthStatusIcon(check.status)}
                              </span>
                              <small className="fw-bold text-capitalize">
                                {checkName.replace(/_/g, ' ')}
                              </small>
                            </div>
                            <small className="text-muted d-block">
                              {check.message}
                            </small>
                            {check.query_time_ms && (
                              <small className="text-muted d-block">
                                Query: {check.query_time_ms}ms
                              </small>
                            )}
                            {check.total_events && (
                              <small className="text-muted d-block">
                                Total Events:{' '}
                                {check.total_events.toLocaleString()}
                              </small>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="col-12 mt-3">
                  <div className="border-top pt-3">
                    <h6 className="mb-3">⚡ Performance Metrics</h6>
                    <div className="row">
                      {performanceData.performance_metrics &&
                        Object.entries(performanceData.performance_metrics).map(
                          ([metric, value]) => (
                            <div
                              key={metric}
                              className="col-12 col-md-3 mb-2"
                              data-testid={`perf-metric-${metric}`}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted text-capitalize">
                                  {metric.replace(/_/g, ' ').replace(' ms', '')}
                                  :
                                </small>
                                <span
                                  className={`badge bg-${
                                    value < 100
                                      ? 'success'
                                      : value < 500
                                        ? 'warning'
                                        : 'danger'
                                  }`}
                                >
                                  {value}ms
                                </span>
                              </div>
                            </div>
                          ),
                        )}
                      <div className="col-12 col-md-3 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Health Check:</small>
                          <span
                            className={`badge bg-${
                              healthData.performance?.health_check_time_ms < 200
                                ? 'success'
                                : 'warning'
                            }`}
                            data-testid="health-check-time"
                          >
                            {healthData.performance?.health_check_time_ms || 0}
                            ms
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-12">
                        <small
                          className="text-muted"
                          data-testid="health-footer"
                        >
                          Last Updated:{' '}
                          {healthData.timestamp
                            ? new Date(healthData.timestamp).toLocaleString()
                            : 'Unknown'}
                          {performanceData.total_events && (
                            <span className="ms-3">
                              Total Events in Database:{' '}
                              {performanceData.total_events.toLocaleString()}
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="text-center text-muted py-3"
                data-testid="health-error"
              >
                <p>Unable to load system health data</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

HealthMonitoringPanel.propTypes = {
  healthData: PropTypes.shape({
    status: PropTypes.string.isRequired,
    timestamp: PropTypes.string,
    checks: PropTypes.object,
    performance: PropTypes.shape({
      health_check_time_ms: PropTypes.number,
    }),
  }),
  performanceData: PropTypes.shape({
    performance_metrics: PropTypes.object,
    total_events: PropTypes.number,
  }),
  isLoading: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
}

export default HealthMonitoringPanel
